import { useQuery } from '@tanstack/react-query';
import api from '../../api';
import { uniquePriceTickers, YahooClosePrices } from '../../utils/yahooPrices';

const YAHOO_REQUEST_TIMEOUT_MS = 2000;

export function useYahooClosePrices(tickers: Array<string | null | undefined>) {
  const normalizedTickers = uniquePriceTickers(tickers).filter(ticker =>
    ticker.endsWith('.US')
  );
  const symbolsKey = normalizedTickers.join(',');

  const yahooQuery = useQuery<YahooClosePrices, Error>({
    queryKey: ['yahooClosePrices', symbolsKey],
    queryFn: async ({ signal }) => {
      const controller = new AbortController();
      const abortFromQuery = () => controller.abort();
      signal?.addEventListener('abort', abortFromQuery, { once: true });
      const timeout = window.setTimeout(
        () => controller.abort(),
        YAHOO_REQUEST_TIMEOUT_MS
      );

      try {
        const response = await api.get('getYahooClosingPrices', {
          params: { symbols: symbolsKey },
          signal: controller.signal,
        });
        const prices = (response.data?.prices || {}) as YahooClosePrices;

        if (Object.keys(prices).length === 0) {
          throw new Error('Yahoo Finance returned no closing prices');
        }

        return prices;
      } finally {
        window.clearTimeout(timeout);
        signal?.removeEventListener('abort', abortFromQuery);
      }
    },
    enabled: normalizedTickers.length > 0,
    retry: false,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  return {
    ...yahooQuery,
    data: yahooQuery.isError ? undefined : yahooQuery.data,
  };
}
